// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * field.ts — the grid, the road, and the one rule the whole game rests on.
 *
 * There is no fixed path. Creeps walk from a Gate to the Heart along whatever is
 * open, and every tower is also a wall — so the road is an emergent property of
 * where you have built, recomputed from scratch whenever the field changes.
 *
 * The load-bearing rule is `canPlace`: **a placement that would leave any Gate
 * with no route to the Heart is illegal**, and so is one that would strand a
 * creep already on the field. That is what turns tower placement from "where
 * does this shoot best" into a geometry puzzle, and it is the promise the game
 * makes to the player in one sentence on the how-to-play screen. A validator
 * that is wrong by one cell breaks it silently, which is why the balance sim
 * audits `sealedFields` at zero tolerance rather than trusting this file.
 *
 * Everything here is pure and allocation-light: `flow()` is called on every
 * candidate cell while the player drags a tower across the field, so it runs a
 * few hundred times a frame on a phone.
 */

import type { Mode } from './modes';

/** A cell the Heart cannot be reached from. */
export const UNREACHABLE = 0xffff;

export interface FieldSpec {
  cols: number;
  rows: number;
  /** Cell indices creeps enter from, on row 0. */
  gates: number[];
  /** Cell index of the Heart, on the last row. */
  heart: number;
  /** cols * rows. */
  size: number;
}

export function fieldOf(mode: Mode): FieldSpec {
  const { cols, rows } = mode;
  return {
    cols,
    rows,
    gates: mode.gateCols.map((c) => c),
    heart: (rows - 1) * cols + mode.heartCol,
    size: cols * rows,
  };
}

export const cellOf = (spec: FieldSpec, col: number, row: number): number => row * spec.cols + col;
export const colOf = (spec: FieldSpec, cell: number): number => cell % spec.cols;
export const rowOf = (spec: FieldSpec, cell: number): number => Math.floor(cell / spec.cols);

/**
 * Neighbour probe order, used by the flow field, the road walk, the drag preview
 * AND creep movement — all four must agree or the road you are shown is not the
 * road the creeps take. Down first because the Heart is always downfield, so the
 * default road is the straight one and the interesting roads are the ones you
 * forced.
 */
const DIRS: readonly [number, number][] = [
  [0, 1], // down
  [-1, 0], // left
  [1, 0], // right
  [0, -1], // up
];

/**
 * Breadth-first distance from every open cell to the Heart.
 *
 * Reused across calls via `out` because the drag preview recomputes this for
 * every cell under the finger; allocating a fresh Uint16Array per candidate is
 * the difference between a smooth drag and a stuttering one on a phone.
 */
export function flow(spec: FieldSpec, blocked: Uint8Array, out?: Uint16Array): Uint16Array {
  const dist = out ?? new Uint16Array(spec.size);
  dist.fill(UNREACHABLE);

  // The Heart itself is never blocked; a tower there would be nonsense.
  const queue = new Int32Array(spec.size);
  let head = 0;
  let tail = 0;
  dist[spec.heart] = 0;
  queue[tail++] = spec.heart;

  while (head < tail) {
    const cell = queue[head++];
    const d = dist[cell] + 1;
    const col = cell % spec.cols;
    const row = (cell - col) / spec.cols;
    for (const [dx, dy] of DIRS) {
      const nc = col + dx;
      const nr = row + dy;
      if (nc < 0 || nc >= spec.cols || nr < 0 || nr >= spec.rows) continue;
      const n = nr * spec.cols + nc;
      if (blocked[n] || dist[n] !== UNREACHABLE) continue;
      dist[n] = d;
      queue[tail++] = n;
    }
  }
  return dist;
}

/**
 * The next cell a creep at `cell` walks to: the neighbour one step closer to the
 * Heart, in DIRS order. Returns -1 at the Heart or from a stranded cell.
 */
export function nextStep(spec: FieldSpec, dist: Uint16Array, cell: number): number {
  const d = dist[cell];
  if (d === 0 || d === UNREACHABLE) return -1;
  const col = cell % spec.cols;
  const row = (cell - col) / spec.cols;
  for (const [dx, dy] of DIRS) {
    const nc = col + dx;
    const nr = row + dy;
    if (nc < 0 || nc >= spec.cols || nr < 0 || nr >= spec.rows) continue;
    const n = nr * spec.cols + nc;
    if (dist[n] === d - 1) return n;
  }
  return -1;
}

/**
 * Every cell on the union of the Gate->Heart roads.
 *
 * The UNION, not the sum, is what "how long is my corridor" means on a two-gate
 * field: Siege's roads share their last stretch, and counting that twice would
 * reward a player for the geometry rather than for the mazing.
 */
export function roadCells(spec: FieldSpec, dist: Uint16Array): number[] {
  const seen = new Set<number>();
  for (const gate of spec.gates) {
    if (dist[gate] === UNREACHABLE) continue;
    let cell = gate;
    let guard = 0;
    while (cell >= 0 && guard++ <= spec.size) {
      seen.add(cell);
      if (cell === spec.heart) break;
      cell = nextStep(spec, dist, cell);
    }
  }
  return [...seen].sort((a, b) => a - b);
}

/** How many cells long the corridor currently is. The number to maximise. */
export function roadLength(spec: FieldSpec, blocked: Uint8Array): number {
  return roadCells(spec, flow(spec, blocked)).length;
}

/** True when every Gate can still reach the Heart. */
export function allGatesOpen(spec: FieldSpec, dist: Uint16Array): boolean {
  for (const gate of spec.gates) if (dist[gate] === UNREACHABLE) return false;
  return true;
}

/** Cells that can never hold a tower, whatever else is true. */
export function isReserved(spec: FieldSpec, cell: number): boolean {
  return cell === spec.heart || spec.gates.includes(cell);
}

export interface PlaceCheck {
  ok: boolean;
  /** Why not, for the rejected-placement toast. */
  reason: 'ok' | 'occupied' | 'reserved' | 'creep' | 'seals' | 'bounds';
  /** The flow field the placement would produce — reused for the drag preview. */
  dist: Uint16Array;
}

/**
 * Can a tower go here?
 *
 * `occupied` is the set of cells a live creep is standing on or walking into. A
 * creep occupies BOTH its current and its next cell while it is between them,
 * because a tower dropped into the cell it is halfway across would teleport it.
 *
 * The seal check is the game's central rule and it covers two distinct things:
 * every Gate must keep a route, AND every creep already on the field must keep
 * one. The second is not implied by the first — a tower can leave the Gate road
 * open while walling off a creep that had already turned a corner behind it.
 */
export function canPlace(
  spec: FieldSpec,
  blocked: Uint8Array,
  cell: number,
  occupied: Iterable<number>,
  scratch?: Uint16Array,
): PlaceCheck {
  const dist = scratch ?? new Uint16Array(spec.size);
  if (cell < 0 || cell >= spec.size) return { ok: false, reason: 'bounds', dist };
  if (isReserved(spec, cell)) return { ok: false, reason: 'reserved', dist };
  if (blocked[cell]) return { ok: false, reason: 'occupied', dist };
  for (const c of occupied) if (c === cell) return { ok: false, reason: 'creep', dist };

  blocked[cell] = 1;
  flow(spec, blocked, dist);
  blocked[cell] = 0;

  if (!allGatesOpen(spec, dist)) return { ok: false, reason: 'seals', dist };
  for (const c of occupied) {
    if (dist[c] === UNREACHABLE) return { ok: false, reason: 'seals', dist };
  }
  return { ok: true, reason: 'ok', dist };
}

/**
 * The corridor a perfect mazer would have built with the SAME number of towers.
 *
 * This is the "what everyone missed" number on the results screen (principle
 * #9), and the choice of reference matters. An absolute maximum corridor length
 * is NP-hard and would also be dishonest — it compares a player who bought 12
 * towers against a layout needing 40. Holding the tower count fixed makes it the
 * question the player actually faced: with what you spent, how long could the
 * road have been?
 *
 * Greedy hill-climbing, not exhaustive: at each step it takes the single legal
 * tower that lengthens the road most. That under-states a true optimum, which is
 * the right direction to be wrong in — the benchmark is beatable, so it can
 * never tell a player they fell short of something impossible.
 */
export function greedyMazeLength(spec: FieldSpec, towers: number): number {
  const blocked = new Uint8Array(spec.size);
  const scratch = new Uint16Array(spec.size);
  let best = roadLength(spec, blocked);

  for (let placed = 0; placed < towers; placed++) {
    let bestCell = -1;
    let bestLen = best;
    for (let cell = 0; cell < spec.size; cell++) {
      if (blocked[cell] || isReserved(spec, cell)) continue;
      blocked[cell] = 1;
      flow(spec, blocked, scratch);
      const len = allGatesOpen(spec, scratch) ? roadCells(spec, scratch).length : -1;
      blocked[cell] = 0;
      if (len > bestLen) {
        bestLen = len;
        bestCell = cell;
      }
    }
    // No single tower improves things — a longer road may still exist behind a
    // two-tower move, but a greedy reference is allowed to stop here.
    if (bestCell < 0) break;
    blocked[bestCell] = 1;
    best = bestLen;
  }
  return best;
}

/** greedyMazeLength is quadratic-ish; results screens ask for it repeatedly. */
const mazeCache = new Map<string, number>();

export function greedyMazeLengthCached(spec: FieldSpec, modeId: string, towers: number): number {
  const key = `${modeId}:${towers}`;
  const hit = mazeCache.get(key);
  if (hit !== undefined) return hit;
  const value = greedyMazeLength(spec, towers);
  mazeCache.set(key, value);
  return value;
}
